import { z } from "zod";

import {
  elementSchema,
  idSchema,
  isEntityField,
  jsonValueSchema,
  type Element,
  type EntityField,
  type JsonValue,
} from "./v2-model";
import {
  committedSchema,
  schemaVersionsSchema,
  snapshotRecordsSchema,
  snapshotSchema,
  submissionSchema,
  type Committed,
  type DrawingSnapshot,
  type SchemaVersions,
  type SnapshotRecords,
  type Submission,
} from "./v2-transactions";
import {
  authorVarsSchema,
  drawingVariablesSchema,
  type AuthorVars,
  type DrawingVariables,
} from "./v2-variables";

/**
 * Protocol 2 (RFC 0002 §10 item 6; `orthos_protocol`), as the messages' JSON forms. `Hello` carries the engine
 * build, `Snapshot` the drawing in canonical form with its author defaults, and `AuthorVars` the author variables a
 * commit's commands changed, to its author alone. A frame of any other version is refused as
 * `unsupported_protocol`, and the app reloads once its queue is synced (FR-COLLAB-036). A `submit` larger than the
 * frame limit `welcome` gives goes as `submitPart`s; one the service will not take as sent is `refused`, and the tab
 * keeps it unsent (RFC 0002 §10.1, "Submissions in parts"). A server frame larger than the tab asks for in `hello`
 * (`maxFrameBytes`) comes as `framePart`s; `snapshotStart` says model space's extents, so the tab draws each part as
 * it arrives; and `open` may name a project file a new drawing starts from (RFC 0002 §10.1, "Opening in pieces").
 */
export const PROTOCOL_VERSION = 2;

/**
 * The snapshot part sizes a tab may ask for in `hello` and a doc service be set to, in bytes. A drawing larger than
 * one part comes as `snapshotStart` and `snapshotPart` messages, each frame within the smaller of the two; the largest
 * is tokio-tungstenite's default frame limit (RFC 0002 §10.1).
 */
export const MIN_SNAPSHOT_PART_BYTES = 64 * 1024;
export const MAX_SNAPSHOT_PART_BYTES = 16 * 1024 * 1024;

export type RejectReason = "invariant" | "permission" | "stale_precondition";
const ERROR_CODES = [
  "unsupported_protocol",
  "unauthenticated",
  "not_found",
  "bad_message",
  "unavailable",
  "too_large",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** Where someone is and what they have selected; the cursor is in drawing units. */
export interface Presence {
  readonly cursor: { readonly x: number; readonly y: number } | null;
  readonly selection: readonly string[];
  readonly view: string | null;
}

export type ClientMessage =
  | {
      readonly type: "hello";
      readonly protocol: number;
      readonly client: string;
      readonly engine: string;
      /** The largest snapshot part this tab takes; absent, the server's own. */
      readonly snapshotPartBytes?: number | undefined;
      /** The largest frame this tab takes whole: a larger one comes as `framePart`s. Absent, every frame whole. */
      readonly maxFrameBytes?: number | undefined;
    }
  | {
      readonly type: "open";
      readonly drawing: string;
      readonly since: number | null;
      /** A project file (`<project>/<name>`) the drawing starts from if the server does not have it yet. */
      readonly from?: string | undefined;
    }
  | { readonly type: "close"; readonly drawing: string }
  | { readonly type: "submit"; readonly submission: Submission }
  /**
   * A run of the bytes of a `submit` frame larger than the service's `maxFrameBytes`, in order from part 0; `bytes`
   * is the whole frame's length. The client core sends these; the app never sees them.
   */
  | {
      readonly type: "submitPart";
      readonly drawing: string;
      readonly transaction: string;
      readonly part: number;
      readonly parts: number;
      readonly bytes: number;
      readonly data: readonly number[];
    }
  | { readonly type: "presence"; readonly drawing: string; readonly presence: Presence }
  | { readonly type: "ping"; readonly nonce: number };

export type ServerMessage =
  | {
      readonly type: "welcome";
      readonly protocol: number;
      readonly user: string;
      readonly canEdit: boolean;
      readonly serverTime: number;
      /** The largest frame the service reads: a larger `submit` goes as `submitPart`s. */
      readonly maxFrameBytes?: number | undefined;
    }
  | { readonly type: "snapshot"; readonly version: number; readonly snapshot: DrawingSnapshot }
  /**
   * A drawing larger than one snapshot part: this, then `parts` `snapshotPart` messages in order, which together are
   * the `snapshot` it would have been. `elements` and `records` count what the parts hold.
   */
  | {
      readonly type: "snapshotStart";
      readonly drawing: string;
      readonly version: number;
      readonly parts: number;
      readonly elements: number;
      readonly records: number;
      readonly vars: DrawingVariables;
      readonly authorDefaults: AuthorVars;
      readonly schemas: SchemaVersions;
      /** Model space's extents, `[minX, minY, maxX, maxY]`, as ZOOM Extents measures them; absent when empty. */
      readonly extents?: readonly [number, number, number, number] | undefined;
      /**
       * A first view (FR-IO-016): the first shard of a drawing still being made from a large project file, for viewing
       * only; the drawing's own snapshot at the same version follows and replaces it. Absent for every other snapshot.
       */
      readonly firstView?: boolean | undefined;
    }
  /** The records and elements after the previous part's, in the snapshot's order; a table may run on. */
  | {
      readonly type: "snapshotPart";
      readonly drawing: string;
      readonly version: number;
      readonly part: number;
      readonly records: SnapshotRecords;
      readonly elements: readonly Element[];
    }
  /**
   * A run of the bytes of a frame larger than the tab's `maxFrameBytes`, in order from part 0, nothing else between;
   * `bytes` is the whole frame's length. The client core puts them back together; the app never sees them.
   */
  | {
      readonly type: "framePart";
      readonly part: number;
      readonly parts: number;
      readonly bytes: number;
      readonly data: readonly number[];
    }
  | { readonly type: "committed"; readonly transaction: Committed }
  /**
   * To a commit's author alone, right after its `Committed`: the author variables its commands changed. Merge them
   * into the author context sent with later intents; they win over what the prediction set.
   */
  | {
      readonly type: "authorVars";
      readonly drawing: string;
      readonly transaction: string;
      readonly vars: AuthorVars;
    }
  | {
      readonly type: "rejected";
      readonly drawing: string;
      readonly transaction: string;
      readonly reason: RejectReason;
      readonly detail: string;
    }
  /**
   * A submission the service will not take as sent (`too_large`, `bad_message`): the tab keeps it and the drawing's
   * later ones, unsent, and says why nothing more is saved.
   */
  | {
      readonly type: "refused";
      readonly drawing: string;
      readonly transaction: string;
      readonly code: ErrorCode;
      readonly message: string;
    }
  | {
      readonly type: "overwritten";
      readonly drawing: string;
      readonly element: string;
      readonly field: EntityField;
      readonly by: string;
      readonly value: JsonValue;
    }
  | {
      readonly type: "lease";
      readonly drawing: string;
      readonly elements: readonly string[];
      readonly holder: string;
      readonly expires: number;
    }
  | {
      readonly type: "presence";
      readonly drawing: string;
      readonly user: string;
      readonly client: string;
      readonly presence: Presence;
    }
  | { readonly type: "left"; readonly drawing: string; readonly client: string }
  /** An open that waits its turn: the drawing is being made from a large project file while others are. */
  | { readonly type: "waiting"; readonly drawing: string; readonly message: string }
  | { readonly type: "pong"; readonly nonce: number }
  | { readonly type: "error"; readonly code: ErrorCode; readonly message: string };

/** The largest value of a byte, in `submitPart`'s data. */
const BYTE_MAX = 255;

export const presenceSchema: z.ZodType<Presence> = z.strictObject({
  cursor: z.strictObject({ x: z.number(), y: z.number() }).nullable(),
  selection: z.array(idSchema),
  view: z.string().nullable(),
});

export const clientMessageSchema: z.ZodType<ClientMessage> = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("hello"),
    protocol: z.number().int(),
    client: z.string().min(1),
    engine: z.string().min(1),
    snapshotPartBytes: z
      .number()
      .int()
      .min(MIN_SNAPSHOT_PART_BYTES)
      .max(MAX_SNAPSHOT_PART_BYTES)
      .optional(),
    maxFrameBytes: z
      .number()
      .int()
      .min(MIN_SNAPSHOT_PART_BYTES)
      .max(MAX_SNAPSHOT_PART_BYTES)
      .optional(),
  }),
  z.strictObject({
    type: z.literal("open"),
    drawing: idSchema,
    since: z.number().int().min(0).nullable(),
    from: z.string().min(1).optional(),
  }),
  z.strictObject({ type: z.literal("close"), drawing: idSchema }),
  z.strictObject({ type: z.literal("submit"), submission: submissionSchema }),
  z.strictObject({
    type: z.literal("submitPart"),
    drawing: idSchema,
    transaction: idSchema,
    part: z.number().int().min(0),
    parts: z.number().int().min(1),
    bytes: z.number().int().min(0),
    data: z.array(z.number().int().min(0).max(BYTE_MAX)),
  }),
  z.strictObject({ type: z.literal("presence"), drawing: idSchema, presence: presenceSchema }),
  z.strictObject({ type: z.literal("ping"), nonce: z.number().int() }),
]);

export const serverMessageSchema: z.ZodType<ServerMessage> = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("welcome"),
    protocol: z.number().int(),
    user: z.string(),
    canEdit: z.boolean(),
    serverTime: z.number(),
    maxFrameBytes: z.number().int().min(1).optional(),
  }),
  z.strictObject({
    type: z.literal("snapshot"),
    version: z.number().int().min(0),
    snapshot: snapshotSchema,
  }),
  z.strictObject({
    type: z.literal("snapshotStart"),
    drawing: idSchema,
    version: z.number().int().min(0),
    parts: z.number().int().min(0),
    elements: z.number().int().min(0),
    records: z.number().int().min(0),
    vars: drawingVariablesSchema,
    authorDefaults: authorVarsSchema,
    schemas: schemaVersionsSchema,
    extents: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
    firstView: z.boolean().optional(),
  }),
  z.strictObject({
    type: z.literal("snapshotPart"),
    drawing: idSchema,
    version: z.number().int().min(0),
    part: z.number().int().min(0),
    records: snapshotRecordsSchema,
    elements: z.array(elementSchema),
  }),
  z.strictObject({
    type: z.literal("framePart"),
    part: z.number().int().min(0),
    parts: z.number().int().min(1),
    bytes: z.number().int().min(0),
    data: z.array(z.number().int().min(0).max(BYTE_MAX)),
  }),
  z.strictObject({ type: z.literal("committed"), transaction: committedSchema }),
  z.strictObject({
    type: z.literal("authorVars"),
    drawing: idSchema,
    transaction: idSchema,
    vars: authorVarsSchema,
  }),
  z.strictObject({
    type: z.literal("rejected"),
    drawing: idSchema,
    transaction: idSchema,
    reason: z.enum(["invariant", "permission", "stale_precondition"]),
    detail: z.string(),
  }),
  z.strictObject({
    type: z.literal("refused"),
    drawing: idSchema,
    transaction: idSchema,
    code: z.enum(ERROR_CODES),
    message: z.string(),
  }),
  z.strictObject({
    type: z.literal("overwritten"),
    drawing: idSchema,
    element: idSchema,
    field: z.custom<EntityField>((field) => typeof field === "string" && isEntityField(field)),
    by: z.string(),
    value: jsonValueSchema,
  }),
  z.strictObject({
    type: z.literal("lease"),
    drawing: idSchema,
    elements: z.array(idSchema),
    holder: z.string(),
    expires: z.number(),
  }),
  z.strictObject({
    type: z.literal("presence"),
    drawing: idSchema,
    user: z.string(),
    client: z.string(),
    presence: presenceSchema,
  }),
  z.strictObject({ type: z.literal("left"), drawing: idSchema, client: z.string() }),
  z.strictObject({ type: z.literal("waiting"), drawing: idSchema, message: z.string() }),
  z.strictObject({ type: z.literal("pong"), nonce: z.number().int() }),
  z.strictObject({
    type: z.literal("error"),
    code: z.enum(ERROR_CODES),
    message: z.string(),
  }),
]);
